import { zodResolver } from '@hookform/resolvers/zod';
import { CaretLeftIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';

import { Button } from '@/components/common/button';
import { useConfirm } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { FormField, PhoneField, SelectField, TextField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';

import { useZipLookup } from '../hooks';
import {
  buildCustomerWriteSchema,
  maskCustomerName,
  maskZip,
  type customerWriteSchema,
} from '../schemas';
import { attributeAddressTitles, dedupeMessages, type ValidationDetail } from '../server-errors';
import type { CustomerWriteBody } from '../types';

import styles from './customer-form.module.scss';

export type CustomerFormValues = CustomerWriteBody;

// The schema's `.default([])` on `addresses` makes the resolver INPUT type differ from the OUTPUT
// (`CustomerWriteBody`): input has `addresses?` optional, output has it required. RHF's form values
// track the input while `handleSubmit` receives the transformed output — hence the 3-generic form.
type CustomerFormInput = z.input<typeof customerWriteSchema>;

export type CustomerFormProps = {
  title: string;
  submitLabel: string;
  defaultValues?: Partial<CustomerWriteBody>;
  validateOnMount?: boolean;
  /** Edit mode: the default (first) address is undeletable — hide its trash (ticket 03, README AC3). */
  lockDefaultAddress?: boolean;
  /** Copy overrides so the shared shell serves both create and edit (ticket 03). */
  submitErrorTitle?: string;
  discardTitle?: string;
  discardSubtitle?: string;
  onSubmit: (values: CustomerWriteBody) => Promise<void>;
  onCancel: () => void;
};

/** Maps a contract address-error code to the address field + message it surfaces under. The
 *  backend validates the first address, so all map onto `addresses.0.*` (grilling Q3). */
/** The scalar inputs a server `details[].field` can be placed on directly. Anything else — an
 *  `addresses` array-level message, or a nested field whose index the wire dropped — is toasted
 *  with the server's own words and marked on whatever control it can still be attributed to
 *  (ADO 118650 / 118942). */
const SCALAR_FIELDS = ['firstName', 'lastName', 'phone', 'email'] as const;
type ScalarField = (typeof SCALAR_FIELDS)[number];
const isScalarField = (field: string): field is ScalarField =>
  (SCALAR_FIELDS as readonly string[]).includes(field);

const ADDRESS_ERROR: Record<string, { field: 'zipcode' | 'state' | 'city'; message: string }> = {
  INVALID_ZIPCODE: { field: 'zipcode', message: 'Enter a valid 5-digit US Zipcode' },
  STATE_ZIPCODE_MISMATCH: { field: 'state', message: 'State does not match the Zipcode' },
  CITY_ZIPCODE_MISMATCH: { field: 'city', message: 'City does not match the Zipcode' },
};

export function CustomerForm({
  title,
  submitLabel,
  defaultValues,
  validateOnMount,
  lockDefaultAddress,
  submitErrorTitle = 'Failed to create customer. Please try again.',
  discardTitle = 'Discard Customer?',
  discardSubtitle = 'Are you sure you want to cancel? All entered details will be lost.',
  onSubmit,
  onCancel,
}: CustomerFormProps) {
  const { confirm } = useConfirm();
  const toast = useToast();

  // Edit: the stored phone may predate the NANP rule; left unchanged it must not block Save (ADR-0035).
  const storedPhone = defaultValues?.phone;
  const schema = useMemo(() => buildCustomerWriteSchema(storedPhone), [storedPhone]);
  const form = useForm<CustomerFormInput, unknown, CustomerWriteBody>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      addresses: [],
      ...defaultValues,
    } as CustomerFormInput,
  });

  // Read during render so RHF's proxy subscribes to these.
  const { isDirty, isValid, isSubmitting, errors } = form.formState;
  // An array-level address rejection (`At least one address is required`) belongs beside the
  // Addresses section, not in a banner above the form — see `handleSubmit` (ADO 118650).
  const addressesError = errors.addresses?.root?.message ?? errors.addresses?.message;

  const addresses = useFieldArray({ control: form.control, name: 'addresses' });

  // AC 102101 "Non-alphabetic characters entered": the character is stripped on the same
  // keystroke, so by the time zod sees the value it's already valid and never raises a schema
  // error — this tracks "the last keystroke on this field got rejected" independently of RHF's
  // validation state, so the AC's inline message can still show (ADO 118649).
  const [nonAlphaHint, setNonAlphaHint] = useState({ firstName: false, lastName: false });
  const maskNameField = (name: 'firstName' | 'lastName', raw: string) => {
    const rejected = /[^A-Za-z ]/.test(raw);
    setNonAlphaHint((prev) => (prev[name] === rejected ? prev : { ...prev, [name]: rejected }));
    return maskCustomerName(raw);
  };

  // Edit mode prefills valid values; RHF won't compute isValid until validation runs,
  // so opt-in trigger once on mount to enable Submit. Create mode omits this.
  useEffect(() => {
    if (validateOnMount) void form.trigger();
  }, [form, validateOnMount]);

  const handleSubmit = async (values: CustomerWriteBody) => {
    try {
      await onSubmit(values);
    } catch (error) {
      if (!(error instanceof ApiError)) {
        toast.error({ title: submitErrorTitle });
        return;
      }
      // AC 102101's copy verbatim — QA reopened 120961 because the shortened form didn't match it.
      if (error.code === 'PHONE_ALREADY_EXISTS') {
        form.setError('phone', {
          message: 'This phone number already exists. Please provide a unique number',
        });
        return;
      }
      if (error.code === 'EMAIL_ALREADY_EXISTS') {
        form.setError('email', { message: 'Email already exists. Please provide unique Email' });
        return;
      }
      if (error.code && error.code in ADDRESS_ERROR) {
        const { field, message } = ADDRESS_ERROR[error.code];
        form.setError(`addresses.0.${field}`, { message });
        return;
      }
      // `VALIDATION_FAILED` is the code the deployed `validation.filter.ts` actually sends for a
      // class-validator rejection; the branch used to test `BAD_REQUEST`, so it was dead and every
      // DTO-level message was replaced by the generic toast (ADO 118788 / 118807 / 118650 / 118854).
      // `BAD_REQUEST` is still accepted in case another endpoint uses it.
      if (
        (error.code === 'VALIDATION_FAILED' || error.code === 'BAD_REQUEST') &&
        Array.isArray(error.details)
      ) {
        const details = error.details as ValidationDetail[];
        // The server cannot name the offending address: the `exceptionFactory` flattens on
        // `error.property`, so an array-level rejection arrives as bare `addresses` and a nested
        // one as bare `title` — the index is gone. Only the scalar fields can be placed on an
        // input directly; `server-errors.ts` recovers the rest where it honestly can.
        const unattributed = details.filter((detail) => !isScalarField(detail.field));
        // The toast goes up first, and deliberately before any `setError`: it is the only part of
        // this branch that is always correct, so it must not be reachable only after the marking
        // below succeeds. The old behaviour — one banner above the actions — was legible but gave
        // no clue which control was at fault (ADO 118650 / 118942). Deduped, because two bad
        // address names return two details carrying the identical string and joining them read
        // "Enter a valid title Enter a valid title" (QA 2026-09-11). A stable key so a repeated
        // submit updates one notice instead of stacking a second.
        if (unattributed.length > 0) {
          toast.error({
            title: dedupeMessages(unattributed).join(' '),
            key: 'customer-form-server-error',
          });
        }
        const titles = (form.getValues('addresses') ?? []).map((address) => address.title ?? '');
        // Which rows a set of bare `title` details refers to, or none when it cannot be known.
        // Marking the whole column was the first attempt and it was worse than marking nothing:
        // with five addresses and two bad names it outlined all five (QA 2026-09-11).
        const titleRows = attributeAddressTitles(unattributed, titles);
        // The rows are marked once, not once per matching detail: two bad names arrive as two
        // details carrying the same message, and looping them would re-set both rows twice.
        const titleMessage = details.find((detail) => detail.field === 'title')?.message;
        if (titleMessage !== undefined) {
          for (const index of titleRows) {
            form.setError(`addresses.${index}.title`, { message: titleMessage });
          }
        }
        for (const detail of details) {
          if (isScalarField(detail.field)) {
            form.setError(detail.field, { message: detail.message });
          } else if (detail.field === 'addresses') {
            // RHF files an array-level message under `errors.addresses.root`.
            form.setError('addresses', { message: detail.message });
          }
          // Any other bare address field (`line1`, `city`, `zipcode`, …) stays unattributed: its
          // rule is not known here, so the toast carries it and no row is outlined.
        }
        if (details.length > 0) return;
      }
      toast.error({ title: submitErrorTitle });
    }
  };

  const handleCancel = async () => {
    if (isDirty) {
      const ok = await confirm({
        title: discardTitle,
        subtitle: discardSubtitle,
        confirmText: 'Discard',
        cancelText: 'Cancel',
        danger: true,
      });
      if (!ok) return;
    }
    onCancel();
  };

  return (
    <form className={styles.form} onSubmit={form.handleSubmit(handleSubmit)} noValidate>
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <button type="button" aria-label="Back" className={styles.backBtn} onClick={handleCancel}>
            <CaretLeftIcon size={20} weight="bold" />
          </button>
          <h1 className={styles.title}>{title}</h1>
        </div>
        <div className={styles.actions}>
          <Button
            htmlType="button"
            variant="outline"
            onClick={handleCancel}
            className={styles.pill}
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            variant="neutral"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            className={styles.pill}
          >
            {submitLabel}
          </Button>
        </div>
      </header>

      <span className={styles.sectionChip}>Customer Details</span>

      <div className={styles.grid}>
        <FormField name="firstName" control={form.control} label="First Name" required>
          {({ field, fieldState }) => (
            <>
              <Input
                id="firstName"
                placeholder="Enter first name"
                status={fieldState.error || nonAlphaHint.firstName ? 'error' : undefined}
                value={field.value ?? ''}
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(maskNameField('firstName', e.target.value))}
              />
              {!fieldState.error && nonAlphaHint.firstName ? (
                <p className={styles.inlineError} role="alert">
                  Only alphabetic characters and spaces are allowed
                </p>
              ) : null}
            </>
          )}
        </FormField>

        <FormField name="lastName" control={form.control} label="Last Name" required>
          {({ field, fieldState }) => (
            <>
              <Input
                id="lastName"
                placeholder="Enter last name"
                status={fieldState.error || nonAlphaHint.lastName ? 'error' : undefined}
                value={field.value ?? ''}
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(maskNameField('lastName', e.target.value))}
              />
              {!fieldState.error && nonAlphaHint.lastName ? (
                <p className={styles.inlineError} role="alert">
                  Only alphabetic characters and spaces are allowed
                </p>
              ) : null}
            </>
          )}
        </FormField>

        <PhoneField name="phone" control={form.control} label="Phone Number" required />

        <TextField name="email" control={form.control} label="Email" placeholder="Enter email" />
      </div>

      {addresses.fields.map((f, i) => (
        <AddressFields
          key={f.id}
          form={form}
          index={i}
          lockDefault={Boolean(lockDefaultAddress) && i === 0}
          onRemove={() => addresses.remove(i)}
        />
      ))}

      {addressesError ? (
        <p className={styles.serverError} role="alert">
          {addressesError}
        </p>
      ) : null}

      <Button
        htmlType="button"
        variant="outline"
        className={styles.pill}
        startIcon={<PlusIcon size={16} />}
        onClick={() => {
          // Clear the server's array-level complaint the moment the admin acts on it, so the
          // message cannot outlive its cause.
          form.clearErrors('addresses');
          addresses.append({ title: '', line1: '', line2: '', city: '', state: '', zipcode: '' });
        }}
      >
        Add Address
      </Button>
    </form>
  );
}

/** One repeatable address block. Owns its ZIP lookup (ticket 113129): a valid 5-digit ZIP
 *  auto-populates State + City and drives the City dropdown options (`cities[]`); an unknown ZIP
 *  surfaces an inline error. Extracted per-row so each block runs its own `useZipLookup`. State is
 *  **read-only**, the lookup's full name verbatim — the backend compares the submitted State
 *  against that same string, so there is nothing an admin could pick that it would accept
 *  (ADO 120879). Field order and read-only State mirror the webstore's address form. */
function AddressFields({
  form,
  index,
  lockDefault,
  onRemove,
}: {
  form: UseFormReturn<CustomerFormInput, unknown, CustomerWriteBody>;
  index: number;
  /** Edit mode: the default (first) address is undeletable — hide its trash (ticket 03, AC3). */
  lockDefault?: boolean;
  onRemove: () => void;
}) {
  const { control, setValue, getValues, setError, clearErrors } = form;
  const zipcode = useWatch({ control, name: `addresses.${index}.zipcode` });
  const currentCity = useWatch({ control, name: `addresses.${index}.city` });
  const state = useWatch({ control, name: `addresses.${index}.state` });
  const { data: lookup, isError } = useZipLookup(zipcode ?? '');

  // Auto-population must react to the admin *typing* a ZIP, never to the prefilled one an existing
  // address mounts with. Without this gate the mount lookup rewrote a stored City that wasn't in the
  // ZIP's `cities[]` — silently editing saved data and marking the form dirty, so Cancel warned
  // "Discard Changes?" on a form nobody touched. A value comparison isn't enough (typing back to the
  // original ZIP must still populate), so the signal comes straight off the input's onChange.
  const userEditedZip = useRef(false);
  useEffect(() => {
    if (!lookup) return;
    // State is derived from the ZIP, never data the admin owns, so an Edit prefill is re-synced
    // too — without dirtying. A row saved while the lookup still answered 2-letter codes holds
    // `TX`, which the backend now rejects as STATE_ZIPCODE_MISMATCH against `Texas` (ADO 120879).
    if (!userEditedZip.current) {
      if (getValues(`addresses.${index}.state`) !== lookup.state) {
        setValue(`addresses.${index}.state`, lookup.state, { shouldValidate: true });
      }
      return;
    }
    setValue(`addresses.${index}.state`, lookup.state, { shouldValidate: true, shouldDirty: true });
    const city = getValues(`addresses.${index}.city`);
    if (!city || !lookup.cities.includes(city)) {
      setValue(`addresses.${index}.city`, lookup.city, { shouldValidate: true, shouldDirty: true });
    }
    clearErrors(`addresses.${index}.zipcode`);
  }, [lookup, index, setValue, getValues, clearErrors]);

  useEffect(() => {
    if (!isError || !userEditedZip.current) return;
    setError(`addresses.${index}.zipcode`, { message: 'Enter a valid 5-digit US Zipcode' });
    // A ZIP that no longer resolves leaves State/City describing the *previous* ZIP. Clear them so
    // the block can't be submitted as a mismatched address (and so the required-field errors point
    // at what actually needs re-picking).
    setValue(`addresses.${index}.state`, '', { shouldValidate: true, shouldDirty: true });
    setValue(`addresses.${index}.city`, '', { shouldValidate: true, shouldDirty: true });
  }, [isError, index, setError, setValue]);

  // City options = the ZIP's cities, plus the current value so a prefilled/chosen city always
  // renders even before a lookup resolves.
  const cityValues = new Set<string>(lookup?.cities ?? []);
  if (currentCity) cityValues.add(currentCity);
  const cityOptions = [...cityValues].map((c) => ({ value: c, label: c }));

  return (
    <section className={styles.addressBlock}>
      <span className={styles.addressChip}>
        Address - {index + 1}
        {lockDefault ? null : (
          <button
            type="button"
            aria-label={`Remove address ${index + 1}`}
            className={styles.trash}
            onClick={onRemove}
          >
            <TrashIcon size={14} />
          </button>
        )}
      </span>

      <div className={styles.grid}>
        <TextField
          name={`addresses.${index}.title`}
          control={control}
          label="Address Name"
          required
          placeholder="Enter address name"
          // AC 102101: the 51st character is stripped, not rejected with an error (ADO 118803) —
          // the DOM enforces the cap on typing and paste, matching `addressWriteSchema`'s max(50).
          maxLength={50}
        />
        <TextField
          name={`addresses.${index}.line1`}
          control={control}
          label="Address Line 1"
          required
          placeholder="Enter address line 1"
          maxLength={100}
        />
        <TextField
          name={`addresses.${index}.line2`}
          control={control}
          maxLength={100}
          label="Address Line 2"
          placeholder="Enter address line 2"
        />
        <FormField name={`addresses.${index}.zipcode`} control={control} label="Zipcode" required>
          {({ field, fieldState }) => (
            <Input
              id={`addresses.${index}.zipcode`}
              placeholder="Enter zipcode"
              inputMode="numeric"
              status={fieldState.error ? 'error' : undefined}
              value={field.value ?? ''}
              ref={field.ref}
              onBlur={field.onBlur}
              onChange={(e) => {
                userEditedZip.current = true;
                field.onChange(maskZip(e.target.value));
              }}
            />
          )}
        </FormField>
        <SelectField
          name={`addresses.${index}.city`}
          control={control}
          label="City"
          required
          placeholder="Select"
          showSearch
          optionFilterProp="label"
          options={cityOptions}
        />
        <TextField
          name={`addresses.${index}.state`}
          control={control}
          label="State"
          required
          placeholder="Auto-filled from zipcode"
          readOnly
          disabled={!state}
        />
      </div>
    </section>
  );
}
